import {
  BRAND_PALETTE_MAX,
  EVENT_CHANNEL_PRESET,
  EVENT_FLOW_PRICES,
  type EventChannel,
  eventChannelValues,
  FORMAT_PRESET_DIMENSIONS,
} from '@pulsify/shared/dto'
import { Check, Plus } from 'lucide-react'
import { formatCredits } from '~/entities/subscription/plans'
import * as m from '~/paraglide/messages'
import { cn } from '~/shared/lib/utils'

const CHANNEL_LABEL: Record<EventChannel, () => string> = {
  'pulsify-cover': () => m.aiFlowChannelPulsifyCover(),
  'instagram-post': () => m.aiFlowChannelInstagramPost(),
  'instagram-story': () => m.aiFlowChannelInstagramStory(),
  'whatsapp-status': () => m.aiFlowChannelWhatsappStatus(),
  facebook: () => m.aiFlowChannelFacebook(),
  'flyer-a6': () => m.aiFlowChannelFlyerA6(),
  'email-header': () => m.aiFlowChannelEmailHeader(),
}

const CHANNEL_RATIO: Record<EventChannel, string> = {
  'pulsify-cover': '3:2',
  'instagram-post': '4:5',
  'instagram-story': '9:16',
  'whatsapp-status': '9:16',
  facebook: '1.91:1',
  'flyer-a6': 'A6',
  'email-header': '3:1',
}

export function channelLabel(channel: EventChannel): string {
  return CHANNEL_LABEL[channel]()
}

function channelAspect(channel: EventChannel): number {
  const { width, height } = FORMAT_PRESET_DIMENSIONS[EVENT_CHANNEL_PRESET[channel]]
  return width / height
}

/** A photo needs its background extended when cropping would cut away more than a third. */
function photoMisfits(photoAspect: number | null, channel: EventChannel): boolean {
  if (!photoAspect) return false
  const target = channelAspect(channel)
  return Math.min(photoAspect, target) / Math.max(photoAspect, target) < 0.67
}

interface ChannelPickerProps {
  value: EventChannel[]
  onChange: (channels: EventChannel[]) => void
  disabled?: boolean
  extend?: EventChannel[]
  onExtendChange?: (channels: EventChannel[]) => void
  photoAspect?: number | null
}

export function ChannelPicker({
  value,
  onChange,
  disabled,
  extend = [],
  onExtendChange,
  photoAspect = null,
}: ChannelPickerProps) {
  const toggle = (channel: EventChannel) => {
    if (channel === 'pulsify-cover') return
    onChange(value.includes(channel) ? value.filter((c) => c !== channel) : [...value, channel])
  }
  return (
    <div className="mt-3 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
      {eventChannelValues.map((channel) => {
        const checked = value.includes(channel)
        const misfit = onExtendChange && checked && photoMisfits(photoAspect, channel)
        return (
          <div key={channel} className="flex flex-col gap-1">
            {/* biome-ignore lint/a11y/useSemanticElements: a whole card toggles, a native checkbox cannot carry its label and ratio */}
            <button
              type="button"
              role="checkbox"
              aria-checked={checked}
              aria-disabled={channel === 'pulsify-cover' || disabled}
              disabled={disabled}
              onClick={() => toggle(channel)}
              className={cn(
                'flex items-center gap-2.5 rounded-xl border px-3 py-2.5 text-left font-medium text-[13.5px] outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring',
                checked ? 'border-brand bg-brand-subtle/60' : 'border-border bg-muted',
              )}
            >
              <span
                className={cn(
                  'flex size-[18px] flex-none items-center justify-center rounded-[5px] border-[1.5px]',
                  checked
                    ? 'border-brand bg-brand text-brand-foreground'
                    : 'border-muted-foreground',
                )}
              >
                {checked && <Check className="size-3" strokeWidth={3} />}
              </span>
              {channelLabel(channel)}
              <span className="ml-auto font-mono text-[10.5px] text-muted-foreground">
                {CHANNEL_RATIO[channel]}
              </span>
            </button>
            {misfit && (
              <label className="flex items-center gap-2 px-3 text-muted-foreground text-xs">
                <input
                  type="checkbox"
                  className="accent-[var(--brand)]"
                  checked={extend.includes(channel)}
                  onChange={(event) =>
                    onExtendChange(
                      event.target.checked
                        ? [...extend, channel]
                        : extend.filter((c) => c !== channel),
                    )
                  }
                />
                {m.aiFlowExtend()}
                <span className="tabular-nums">
                  {m.aiFlowPrice({ credits: formatCredits(EVENT_FLOW_PRICES.extendBackground) })}
                </span>
              </label>
            )}
          </div>
        )
      })}
    </div>
  )
}

interface BrandColorsProps {
  colors: string[]
  onChange: (colors: string[]) => void
}

/** The profile's brand colours, read from its logo and changeable in place. */
export function BrandColors({ colors, onChange }: BrandColorsProps) {
  return (
    <span className="inline-flex items-center gap-1.5 align-middle">
      {colors.map((color, index) => (
        <label
          // biome-ignore lint/suspicious/noArrayIndexKey: two swatches may share a colour while one is being changed
          key={index}
          className="relative size-4 cursor-pointer rounded-full ring-1 ring-border"
          style={{ background: color }}
          title={m.aiFlowBrandColorEdit()}
        >
          <input
            type="color"
            value={color}
            aria-label={m.aiFlowBrandColorEdit()}
            className="absolute inset-0 cursor-pointer opacity-0"
            onChange={(event) =>
              onChange(colors.map((c, i) => (i === index ? event.target.value : c)))
            }
          />
        </label>
      ))}
      {colors.length < BRAND_PALETTE_MAX && (
        <label
          className="relative flex size-4 cursor-pointer items-center justify-center rounded-full border border-muted-foreground border-dashed text-muted-foreground"
          title={m.aiFlowBrandColorAdd()}
        >
          <Plus className="size-2.5" />
          <input
            type="color"
            aria-label={m.aiFlowBrandColorAdd()}
            className="absolute inset-0 cursor-pointer opacity-0"
            onChange={(event) => onChange([...colors, event.target.value])}
          />
        </label>
      )}
    </span>
  )
}
